import { ShellIconMic, ShellIconPause, ShellIconPlay, Spinner } from '@zrsystem/ui';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
} from 'react';

import { beginChatAudioPlayback, endChatAudioPlayback } from '@/shared/chat/chat-audio-playback.js';
import '@/shared/chat/chat-voice-note.css';

const WAVE_BARS = 28;
const SPEEDS = [1, 1.5, 2] as const;

export interface ChatVoiceNoteProps {
  readonly audioUrl: string;
  readonly messageId: string;
  readonly durationSeconds?: number;
  readonly own?: boolean;
  readonly pending?: boolean;
}

function formatVoiceClock(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function waveformHeights(seed: string): number[] {
  let hash = 2166136261;
  for (const char of seed) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  const bars: number[] = [];
  for (let index = 0; index < WAVE_BARS; index += 1) {
    hash = Math.imul(hash ^ (hash >>> 16), 2246822519);
    const unit = ((hash >>> 0) % 100) / 100;
    bars.push(5 + Math.round(unit * 17));
  }
  return bars;
}

/** Voice note laid out like WhatsApp Web: play, waveform, duration, microphone. */
export function ChatVoiceNote({
  audioUrl,
  messageId,
  durationSeconds,
  own = false,
  pending = false,
}: ChatVoiceNoteProps): ReactElement {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [loading, setLoading] = useState(audioUrl.length > 0);
  const [failed, setFailed] = useState(false);
  const [speedIndex, setSpeedIndex] = useState(0);
  const bars = useMemo(() => waveformHeights(messageId), [messageId]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio === null || audioUrl.length === 0) return;

    const handle = {
      id: messageId,
      pause: () => {
        audio.pause();
      },
    };

    const onLoadedMetadata = () => {
      setLoading(false);
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };
    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const onPlay = () => {
      setPlaying(true);
    };
    const onPause = () => {
      setPlaying(false);
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
      endChatAudioPlayback(handle);
    };
    const onError = () => {
      setLoading(false);
      setFailed(true);
    };

    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('durationchange', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('durationchange', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      endChatAudioPlayback(handle);
    };
  }, [audioUrl, messageId]);

  const knownDuration = duration > 0.5 ? duration : (durationSeconds ?? 0);
  const progress = knownDuration > 0 ? Math.min(1, Math.max(0, currentTime / knownDuration)) : 0;
  const playedBars = Math.round(progress * WAVE_BARS);
  const clock =
    playing || currentTime > 0.5
      ? formatVoiceClock(currentTime)
      : knownDuration > 0
        ? formatVoiceClock(knownDuration)
        : '0:00';
  const speed = SPEEDS[speedIndex] ?? 1;

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (audio === null || failed || pending || audioUrl.length === 0) return;

    const handle = {
      id: messageId,
      pause: () => {
        audio.pause();
      },
    };

    if (playing) {
      audio.pause();
      endChatAudioPlayback(handle);
      return;
    }

    if (knownDuration > 0 && audio.currentTime >= knownDuration - 0.2) {
      audio.currentTime = 0;
    }

    audio.playbackRate = speed;
    beginChatAudioPlayback(handle);
    try {
      await audio.play();
    } catch {
      endChatAudioPlayback(handle);
    }
  }, [audioUrl.length, failed, knownDuration, messageId, pending, playing, speed]);

  const handleSeek = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const audio = audioRef.current;
      if (audio === null || knownDuration <= 0 || failed) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * knownDuration;
      setCurrentTime(audio.currentTime);
    },
    [failed, knownDuration],
  );

  function cycleSpeed(): void {
    const next = (speedIndex + 1) % SPEEDS.length;
    setSpeedIndex(next);
    const audio = audioRef.current;
    const rate = SPEEDS[next];
    if (audio !== null && rate !== undefined) {
      audio.playbackRate = rate;
    }
  }

  const rootClass = own ? 'chat-voice chat-voice--own' : 'chat-voice';

  return (
    <div className={rootClass}>
      {audioUrl.length > 0 ? (
        <audio ref={audioRef} preload="metadata" src={audioUrl} className="chat-voice__element" />
      ) : null}
      <button
        type="button"
        className="chat-voice__play"
        onClick={() => {
          void togglePlay();
        }}
        aria-label={playing ? 'Pausar áudio' : 'Ouvir áudio'}
        disabled={loading || pending || failed || audioUrl.length === 0}
      >
        {loading || pending ? (
          <Spinner aria-label="Carregando áudio" />
        ) : playing ? (
          <ShellIconPause size={16} />
        ) : (
          <ShellIconPlay size={16} />
        )}
      </button>
      <div className="chat-voice__body">
        <button
          type="button"
          className="chat-voice__wave"
          onClick={handleSeek}
          aria-label="Posição do áudio"
          disabled={failed || knownDuration <= 0}
        >
          {bars.map((height, index) => (
            <span
              key={index}
              className={
                index < playedBars ? 'chat-voice__bar chat-voice__bar--played' : 'chat-voice__bar'
              }
              style={{ height }}
            />
          ))}
        </button>
        <div className="chat-voice__meta">
          <span className="chat-voice__time">{failed ? 'Falha ao carregar áudio' : clock}</span>
          {failed ? null : (
            <button
              type="button"
              className="chat-voice__speed"
              aria-label={`Velocidade ${speed}×`}
              onClick={cycleSpeed}
            >
              {speed}×
            </button>
          )}
        </div>
      </div>
      <span className="chat-voice__mic" aria-hidden>
        <ShellIconMic size={16} />
      </span>
    </div>
  );
}
